/**
 * Permissions อิงจาก docs/specs (A1–A7)
 * Format: resource:action
 */
export const Permissions = {
  users: {
    create: "users:create",
    read: "users:read",
    update: "users:update",
    delete: "users:delete",
    ban: "users:ban",
    assign_role: "users:assign_role",
  },
  audit: {
    read: "audit:read",
  },
  // A1. Shop Management
  shops: {
    list: "shops:list",
    read: "shops:read",
    create: "shops:create",
    update: "shops:update",
    verify: "shops:verify",
    suspend: "shops:suspend",
  },
  branches: {
    list: "branches:list",
    read: "branches:read",
    create: "branches:create",
    update: "branches:update",
    delete: "branches:delete",
  },
  // A2. Mechanic Management
  mechanics: {
    list: "mechanics:list",
    read: "mechanics:read",
    verify: "mechanics:verify",
    suspend: "mechanics:suspend",
  },
  // A3. Fee & Commission Management
  fee_rules: {
    list: "fee_rules:list",
    read: "fee_rules:read",
    create: "fee_rules:create",
    update: "fee_rules:update",
  },
  categories: {
    list: "categories:list",
    read: "categories:read",
    create: "categories:create",
    update: "categories:update",
    delete: "categories:delete",
  },
  // C1. Master Catalog (Admin)
  catalog: {
    read: "catalog:read",
    create: "catalog:create",
    update: "catalog:update",
    delete: "catalog:delete",
  },
  // A4. Marketplace Moderation
  listings: {
    list: "listings:list",
    read: "listings:read",
    update: "listings:update",
    moderate: "listings:moderate",
  },
  reports: {
    list: "reports:list",
    read: "reports:read",
    update: "reports:update",
  },
  moderation: {
    history: "moderation:history",
  },
  // A5. Dispute Management
  disputes: {
    list: "disputes:list",
    read: "disputes:read",
    update: "disputes:update",
    resolve: "disputes:resolve",
  },
  // A6. Platform Analytics
  analytics: {
    read: "analytics:read",
  },
  // A7. System Governance
  roles: {
    list: "roles:list",
    read: "roles:read",
    create: "roles:create",
    update: "roles:update",
    delete: "roles:delete",
  },
  approvals: {
    list: "approvals:list",
    read: "approvals:read",
    approve: "approvals:approve",
    reject: "approvals:reject",
  },
  // A8. Wallet & Settlement
  wallet: {
    overview: "wallet:overview",
    list: "wallet:list",
    read: "wallet:read",
    transactions: "wallet:transactions",
  },
  top_up: {
    list: "top_up:list",
    approve: "top_up:approve",
    reject: "top_up:reject",
  },
  refund: {
    list: "refund:list",
    create: "refund:create",
    approve: "refund:approve",
  },
  payout: {
    list: "payout:list",
    read: "payout:read",
    create: "payout:create",
    process: "payout:process",
  },
  wallet_fee: {
    list: "wallet_fee:list",
    read: "wallet_fee:read",
    create: "wallet_fee:create",
    update: "wallet_fee:update",
  },
  compliance: {
    read: "compliance:read",
    update: "compliance:update",
  },
  suspicious: {
    list: "suspicious:list",
    review: "suspicious:review",
  },
  reconciliation: {
    list: "reconciliation:list",
    read: "reconciliation:read",
    match: "reconciliation:match",
    resolve: "reconciliation:resolve",
  },
} as const;

export const ALL_PERMISSIONS = Object.entries(Permissions).flatMap(
  ([resource, actions]) =>
    Object.entries(actions).map(([action, id]) => ({ id, resource, action })),
);

export type PermissionId = (typeof ALL_PERMISSIONS)[number]["id"];

// Human-friendly labels for rendering in UI
export const RESOURCE_LABELS: Record<string, string> = {
  users: "ຜູ້ໃຊ້",
  audit: "ບັນທຶກການກວດກາ",
  shops: "ຮ້ານຄ້າ",
  branches: "ສາຂາ",
  mechanics: "ຊ່າງແປງ",
  fee_rules: "ກົດຄ່າທຳນຽມ",
  categories: "ຫມວດຫມູ່",
  catalog: "Catalog",
  listings: "ລາຍການສິນຄ້າ",
  reports: "ລາຍງານ",
  moderation: "ປະຫວັດການກວດກາ",
  disputes: "ຂໍ້ພິພາດ",
  analytics: "ວິເຄາະ",
  roles: "ບົດບາດ",
  approvals: "ການອະນຸມັດ",
  wallet: "ກະເປົາເງິນ",
  top_up: "ເຕີມເງິນ",
  refund: "ຄືນເງິນ",
  payout: "ຈ່າຍເງິນ",
  wallet_fee: "ຄ່າທຳນຽມກະເປົາ",
  compliance: "ຄວາມສອດຄ່ອງ",
  suspicious: "ລາຍການສົງໄສ",
  reconciliation: "ການຕິດຕາມກັນ",
};

export const ACTION_LABELS: Record<string, string> = {
  create: "ສ້າງ",
  read: "ເບິ່ງ",
  update: "ແກ້ໄຂ",
  delete: "ລຶບ",
  ban: "ລະງັບ",
  assign_role: "ມອບບົດບາດ",
  list: "ລາຍການ",
  verify: "ຢືນຢັນ",
  suspend: "ລະງັບ",
  moderate: "ກວດກາ",
  resolve: "ຕັດສິນ",
  approve: "ອະນຸມັດ",
  reject: "ປະຕິເສດ",
  overview: "ເບິ່ງພາບລວມ",
  transactions: "ລາຍການທຸລະກຳ",
  process: "ດຳເນີນການ",
  match: "ຈັບຄູ່",
  review: "ກວດສອບ",
  history: "ປະຫວັດ",
  all: "ທັງໝົດ",
};

export function getResourceLabel(resource: string): string {
  return RESOURCE_LABELS[resource] ?? resource;
}

export function getActionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function getPermissionLabel(id: PermissionId): string {
  const [resource, action] = (id as string).split(":");
  return `${getActionLabel(action ?? "")} ${getResourceLabel(resource ?? "")}`;
}

export function getPermissionLabels(ids: PermissionId[]): string[] {
  return ids.map((id) => getPermissionLabel(id));
}
